import { Link, usePage } from "@inertiajs/react"
import { cn } from "@/lib/utils"
import { SeoHead } from "@/components/SeoHead"
import { organizationNode, serviceNode, personHarryNode, faqPageNode } from "@/lib/seo"
import type { PageProps } from "@/types/inertia"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"
import { CredibilityStrip } from "@/components/marketing/sections/CredibilityStrip"
import { GuestPipelineBoundary } from "@/components/marketing/sections/GuestPipelineBoundary"
import { ForWhom } from "@/components/marketing/sections/ForWhom"
import { FinalCta } from "@/components/marketing/sections/FinalCta"
import { track } from "@/lib/analytics"

const DESCRIPTION =
  "Done-for-you podcast production for founders, coaches, and consultants. Strategy, guest preparation, weekly video episodes, and content multiplication in one six-month partnership."

// The seven pillars, in this page's own words. Kept in sync with the homepage
// Offer section in substance, not in phrasing.
const INCLUDED = [
  {
    name: "Authority strategy",
    body: "The show's business role, audience, offer alignment, and positioning, decided before anything gets recorded.",
  },
  {
    name: "Relationship map",
    body: "The categories of people worth building around: future clients, referral partners, collaborators, and credibility partners.",
  },
  {
    name: "Guest pipeline intelligence",
    body: "Researched and prioritized guest opportunities each month, with context and outreach templates so invitations feel considered.",
  },
  {
    name: "Conversation system",
    body: "Host briefs, guest notes, interview questions, and episode angles so every recording has a clear purpose.",
  },
  {
    name: "Weekly video production",
    body: "Polished weekly video episodes, fully edited, published, and packaged.",
  },
  {
    name: "Content multiplication",
    body: "Each conversation repurposed into show notes, clips, posts, and assets that outlive the episode.",
  },
  {
    name: "Monthly strategic review",
    body: "Content momentum, guest strategy, and relationship opportunities reviewed together every month.",
  },
]

// The operating cadence. Every claim here maps to a pillar above — no invented
// volumes or turnaround times.
const RHYTHM = [
  {
    cadence: "Every week",
    title: "An episode goes out",
    body: "Recorded, edited, published, and packaged. You show up for the conversation; the production around it is handled.",
  },
  {
    cadence: "Every month",
    title: "New guest opportunities, researched",
    body: "A fresh set of prioritized guest opportunities with context and outreach templates, so the invitations you send are specific rather than generic.",
  },
  {
    cadence: "Every month",
    title: "A strategic review together",
    body: "Content momentum, guest strategy, and relationship opportunities looked at as a whole, then next steps agreed.",
  },
  {
    cadence: "Ongoing",
    title: "Each conversation multiplied",
    body: "Show notes, clips, posts, and follow-up assets from every episode, so one recording keeps working after it publishes.",
  },
]

// Launch sequence. Phases group the setup items listed on /pricing (SETUP) in
// the order they happen. No per-phase durations: the only timing fact on the
// site is "about six weeks from kickoff to the first episode" overall.
const LAUNCH_PHASES = [
  {
    title: "Strategy and format",
    body: "Launch strategy and a format consultation, plus coaching on format, interviews and scheduling.",
  },
  {
    title: "Identity and assets",
    body: "Cover art design, intro and outro music selection, and the voice-over script with guidance for the talent.",
  },
  {
    title: "Equipment and hosting",
    body: "Equipment recommendations and training, then hosting platform selection and setup.",
  },
  {
    title: "Trailer and directories",
    body: "A trailer episode produced with you, submission to every major directory, and a podcast section built on your website.",
  },
  {
    title: "First episode",
    body: "The first episode ships, and the weekly cycle begins: you record, we produce, the episode goes out.",
  },
]

// Buying-stage questions. Deliberately different from the homepage FAQ, which
// answers "why podcast at all" rather than "what is it like to hire you".
const FAQS = [
  {
    q: "We already have a podcast. Can you take it over?",
    a: "Yes. We reposition existing shows for stronger structure, better guest strategy, and clearer business alignment, and we help new shows start with the right foundation. An existing back catalogue is an advantage, not a complication.",
  },
  {
    q: "We already have someone editing. Is this still worth it?",
    a: "Possibly not, and it's worth being honest about that. Production is one of seven parts. If a finished file is all you need, a good editor is cheaper and enough. This is built for shows where the strategy, the guest selection, and what happens after publishing matter as much as the edit.",
  },
  {
    q: "What do you need from me?",
    a: "Showing up to record, approving guests, sending the invitations, scheduling, and the follow-up. Those stay with you because they land better coming from the host. Strategy, research, preparation, production, and multiplication are ours.",
  },
  {
    q: "Do episodes have to be video?",
    a: "Video is included by default. It produces the most flexible, repurposable content and the strongest authority signal across the platforms your audience already uses.",
  },
  {
    q: "How long is the engagement?",
    a: "Six months, long enough to build real momentum in positioning, guest relationships, and a library of trust-rich content rather than a burst that fades. You can renew for another six months at the end.",
  },
  {
    q: "How long until the first episode?",
    a: "About six weeks from kickoff. Setup covers launch strategy and format, cover art, music and the voice-over script, equipment and hosting, a trailer episode, and directory submission. After that the cycle repeats weekly: you record, we produce, the episode ships. If you already have a show running, setup is reviewed and reduced, because several of those steps are already behind you.",
  },
  {
    q: "What does it cost?",
    a: "There's no public pricing. The partnership is tailored to your show and goals, so we qualify fit and budget privately through the application, then discuss specifics together on the Fit Call.",
  },
]

export default function PodcastProduction() {
  const base = (usePage<PageProps>().props.app_url || "https://fullcast.co").replace(/\/$/, "")
  const handleApplyClick = () => track("CTA Click")

  return (
    <MarketingLayout>
      <SeoHead
        title="Podcast Production for Founders and Experts"
        description={DESCRIPTION}
        jsonLd={[
          organizationNode(base),
          serviceNode(base),
          personHarryNode(base),
          faqPageNode(FAQS),
        ]}
      />

      {/* Hero — BLUF. Direct answer to "what is this and who is it for" before
          any narrative, for readers and AI crawlers alike. */}
      <Section containerClassName="max-w-3xl">
        <Eyebrow>FullCast · Podcast Growth Partnership</Eyebrow>
        <h1 className="display-1 mt-4">
          Done-for-you podcast production for founders and experts
        </h1>
        <p className="mt-5 text-lg text-ink-body">
          FullCast is a boutique, founder-led podcast agency. We run strategy,
          guest preparation, weekly video production, and content multiplication
          as a single six-month partnership, so an established expert can show
          up for the conversation and hand off everything around it.
        </p>
        <p className="mt-4 text-ink-muted">
          It is deliberately not cheap editing by the episode. The show is
          treated as a system for building authority and relationships, which
          means the strategy matters as much as the audio.
        </p>
        <div className="mt-8">
          <Link
            href="/apply"
            onClick={handleApplyClick}
            className="inline-block rounded-xl bg-ink-display px-6 py-3 text-page no-underline hover:bg-ink-body"
          >
            Apply for a Fit Call →
          </Link>
        </div>
      </Section>

      <CredibilityStrip />

      {/* The reframe — why this sits apart from commodity production */}
      <Section className="border-t border-hairline bg-surface" containerClassName="max-w-3xl">
        <Eyebrow>The difference</Eyebrow>
        <h2 className="display-2 mt-4">
          Most podcast production stops at the audio file.
        </h2>
        <p className="mt-5 text-lg text-ink-body">
          Editing is the commodity part. Plenty of people do it well and cheaply,
          and if a finished file is all you need, you do not need a partnership
          to get one.
        </p>
        <p className="mt-4 text-ink-muted">
          The harder work is deciding who should be on the show, why the
          conversation matters to the business, and what happens to each episode
          after it publishes. That is the part that turns a podcast from a
          content obligation into a relationship engine, and it is the part
          FullCast is built around.
        </p>
      </Section>

      {/* What's included */}
      <Section className="border-t border-hairline">
        <div className="max-w-2xl">
          <Eyebrow>What's included</Eyebrow>
          <h2 className="display-2 mt-4">Seven parts, one partnership</h2>
          <p className="mt-5 text-lg text-ink-body">
            Six months, because positioning, guest relationships, and a library
            of trust-rich content take longer than a launch burst to compound.
          </p>
        </div>

        <ol className="mt-12 grid gap-px overflow-hidden rounded-xl border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-3">
          {INCLUDED.map((item, i) => (
            <li
              key={item.name}
              className={cn(
                "bg-page p-6",
                // Last of 7 would otherwise sit alone with dead space beside it.
                i === INCLUDED.length - 1 && "sm:col-span-2 lg:col-span-3",
              )}
            >
              <div className="font-display text-sm font-semibold text-accent-display">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="mt-3">{item.name}</h3>
              <p className="mt-2 text-sm text-ink-muted">{item.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      {/* From kickoff to first episode — the launch sequence. Timing is the one
          fact from /pricing ("about six weeks"); phases carry no week numbers. */}
      <Section className="border-t border-hairline" containerClassName="max-w-3xl">
        <Eyebrow>Getting started</Eyebrow>
        <h2 className="display-2 mt-4">From kickoff to first episode</h2>
        <p className="mt-5 text-lg text-ink-body">
          Setup takes about six weeks from kickoff to the first episode. After
          that the cycle repeats weekly. FullCast has launched 130+ shows since
          2015.
        </p>

        <ol className="mt-10 grid gap-5">
          {LAUNCH_PHASES.map((phase, i) => (
            <li
              key={phase.title}
              className="rounded-xl border border-hairline bg-surface p-7"
            >
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-accent-display">
                Step {i + 1}
              </div>
              <h3 className="mt-3">{phase.title}</h3>
              <p className="mt-2 text-ink-muted">{phase.body}</p>
            </li>
          ))}
        </ol>

        <p className="mt-8 text-ink-muted">
          Already running a show? Setup is reviewed and reduced, because several
          of these steps are already behind you.
        </p>
      </Section>

      {/* The rhythm — what working together actually looks like month to month */}
      <Section className="border-t border-hairline bg-surface">
        <div className="max-w-2xl">
          <Eyebrow>The rhythm</Eyebrow>
          <h2 className="display-2 mt-4">What a month actually looks like</h2>
          <p className="mt-5 text-lg text-ink-body">
            The partnership runs on a steady cadence rather than a burst of
            activity at the start.
          </p>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2">
          {RHYTHM.map((item) => (
            <div
              key={item.title}
              className="rounded-xl border border-hairline bg-page p-7"
            >
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-accent-display">
                {item.cadence}
              </div>
              <h3 className="mt-3">{item.title}</h3>
              <p className="mt-2 text-ink-muted">{item.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <GuestPipelineBoundary />
      <ForWhom />

      {/* Hiring-stage FAQ */}
      <Section className="border-t border-hairline">
        <div className="max-w-2xl">
          <Eyebrow>Before you apply</Eyebrow>
          <h2 className="display-2 mt-4">Questions people ask at this stage</h2>
        </div>

        <dl className="mt-12 grid gap-5 md:grid-cols-2">
          {FAQS.map((faq) => (
            <div
              key={faq.q}
              className="rounded-xl border border-hairline bg-surface p-7"
            >
              <dt>
                <h3>{faq.q}</h3>
              </dt>
              <dd className="mt-3 text-ink-muted">{faq.a}</dd>
            </div>
          ))}
        </dl>
      </Section>

      <FinalCta />
    </MarketingLayout>
  )
}
